import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import Card from "react-bootstrap/Card"; // ✅ Added Card
import { FaMoneyBillWave, FaFileInvoiceDollar, FaArrowLeft } from "react-icons/fa";
import "./expense-info.css";

const INFRA_CATEGORIES = [
  "Main Room",
  "Bathroom",
  "Solar",
  "Masjid",
  "Main Gate",
];

const LAB_CATEGORIES = [
  "Masonry Work",
  "Construction Work",
  "Electrical Work",
  "Sanitary Work",
  "Maintenance Work",
  "Painting Work",
  "Rent",
  "Utilities",
  "Salaries",
  "Office Supplies",
  "Marketing",
  "Travel",
  "Software",
  "Equipment",
  "Miscellaneous",
];

const AddExpense = () => {
  const navigate = useNavigate();

  const [expenseType, setExpenseType] = useState("Lab");
  const [expenseDate, setExpenseDate] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [paidTo, setPaidTo] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [notes, setNotes] = useState("");
  const [billImage, setBillImage] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertVariant, setAlertVariant] = useState("success");

  const categoryOptions =
    expenseType === "Infra" ? INFRA_CATEGORIES : LAB_CATEGORIES;

  const paymentMethods = ["Cash", "Bank Transfer", "Cheque", "Card", "Other"];

  const resetForm = () => {
    setExpenseDate("");
    setCategory("");
    setDescription("");
    setAmount("");
    setPaidTo("");
    setPaymentMethod("Cash");
    setNotes("");
    setBillImage(null);
  };

  const handleTabChange = (tab) => {
    if (tab === expenseType) return;
    setExpenseType(tab);
    setCategory("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!category) {
      setAlertMessage("Please select a category.");
      setAlertVariant("danger");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000);
      return;
    }

    const formData = new FormData();
    formData.append("type", expenseType);
    formData.append("expenseDate", expenseDate);
    formData.append("category", category);
    formData.append("description", description);
    formData.append("amount", amount);
    formData.append("paidTo", paidTo);
    formData.append("paymentMethod", paymentMethod);
    formData.append("notes", notes);

    try {
      const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
      if (userInfo?._id) formData.append("addedBy", userInfo._id);
      if (userInfo?.name) formData.append("addedByName", userInfo.name);
    } catch (_) {}

    if (billImage) formData.append("billImage", billImage);

    try {
      await axios.post(
        "https://backend.ems.hiradentallab.com/api/expenses/create",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setAlertMessage("Expense added successfully!");
      setAlertVariant("success");
      setShowAlert(true);
      resetForm();
      setTimeout(() => navigate("/expense-info"), 2000);
    } catch (error) {
      setAlertMessage(
        error.response?.data?.message || "Error adding expense. Please try again."
      );
      setAlertVariant("danger");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000);
      console.error("Error adding expense:", error);
    }
  };

  return (
    // ✅ Fixed Wrapper
    <div className="expense-page-wrap">

      {/* ✅ Fixed Titles */}
      <h1 className="expense-page-title">
        <FaMoneyBillWave /> Add Expense
      </h1>
      <hr className="expense-divider" />

      <h1 className="expense-page-subtitle">
        <FaFileInvoiceDollar /> Expense Details
      </h1>
      <hr className="expense-divider" />

      {/* ✅ Wrapped Form in Card to match your List page theme */}
      <Card className="expense-filter-card" style={{ padding: "24px" }}>
        <Form onSubmit={handleSubmit}>
          {showAlert && (
            <Alert variant={alertVariant} onClose={() => setShowAlert(false)} dismissible>
              {alertMessage}
            </Alert>
          )}

          {/* ============ TABS ============ */}
          <div style={{ display: "flex", gap: "8px", marginBottom: "20px", borderBottom: "2px solid #e6eefc", paddingBottom: "0px", flexWrap: "wrap" }}>
            {["Infra", "Lab"].map((t) => {
              const isActive = expenseType === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => handleTabChange(t)}
                  style={{
                    padding: "10px 24px",
                    borderRadius: "6px 6px 0 0",
                    border: "1px solid #004aad",
                    borderBottom: isActive ? "2px solid #ffffff" : "1px solid #004aad",
                    marginBottom: "-2px",
                    background: isActive ? "#004aad" : "#ffffff",
                    color: isActive ? "#ffffff" : "#004aad",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {t}
                </button>
              );
            })}
          </div>

          <Row className="mb-3">
            <Form.Group as={Col} xs={12} md={6} controlId="expenseDate">
              <Form.Label>Expense Date <span style={{color: "red"}}>*</span> :</Form.Label>
              <Form.Control
                type="date"
                value={expenseDate}
                onChange={(e) => setExpenseDate(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group as={Col} xs={12} md={6} controlId="category">
              <Form.Label>{expenseType} Category <span style={{color: "red"}}>*</span> :</Form.Label>
              <Form.Control
                as="select"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                required
              >
                <option value="">--Select Category--</option>
                {categoryOptions.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} xs={12} md={6} controlId="description">
              <Form.Label>Description <span style={{color: "red"}}>*</span> :</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Expense Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group as={Col} xs={12} md={6} controlId="amount">
              <Form.Label>Amount (PKR) <span style={{color: "red"}}>*</span> :</Form.Label>
              <Form.Control
                type="number"
                placeholder="Enter Amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} xs={12} md={6} controlId="paidTo">
              <Form.Label>Paid To (Vendor / Person) :</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Vendor / Person Name"
                value={paidTo}
                onChange={(e) => setPaidTo(e.target.value)}
              />
            </Form.Group>

            <Form.Group as={Col} xs={12} md={6} controlId="paymentMethod">
              <Form.Label>Payment Method :</Form.Label>
              <Form.Control
                as="select"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                {paymentMethods.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} xs={12} controlId="notes">
              <Form.Label>Notes :</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Additional Notes (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Form.Group>
          </Row>

          <Row className="mb-4">
            <Form.Group as={Col} xs={12} md={6} controlId="billImage">
              <Form.Label>Bill Image :</Form.Label>
              <Form.Control
                type="file"
                accept=".png, .jpg, .jpeg, .pdf"
                onChange={(e) => setBillImage(e.target.files[0])}
              />
              <small className="text-muted">Allowed: PNG, JPG, JPEG, PDF</small>
            </Form.Group>
          </Row>

          <div style={{ display: "flex", gap: "10px" }}>
            <Button className="expense-add-btn" type="submit">
              Add Expense
            </Button>
            <Button variant="secondary" onClick={() => navigate("/expense-info")}>
              Cancel
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default AddExpense;